/**
 * SupervisorMonitor — live call monitoring for supervisors (React).
 *
 * Renders an ear icon in the header (supervisor+ roles only) that opens a
 * dropdown listing every active call in the tenant. A supervisor can Listen,
 * Whisper, or Barge into any in-progress call, switch mode while joined, and
 * Leave.
 *
 * All state + the apex-voice command protocol live in the framework-agnostic
 * controller `client/lib/supervisor-monitor.ts`; this component is a thin
 * subscriber (useSyncExternalStore) over it. The controller is also what
 * `softphone.ts` consults to auto-accept the SDK call apex-voice places when
 * the supervisor joins a conference.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Ear } from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';
import {
  subscribe,
  getSnapshot,
  setMonitorIdentity,
  registerVoiceEvents,
  openPanel,
  closePanel,
  joinCall,
  changeMode,
  leaveCall,
  SUPERVISOR_ROLES,
  type SupervisorCallInfo,
  type SupervisorMode,
} from '../../../lib/supervisor-monitor';

const MODES: SupervisorMode[] = ['listen', 'whisper', 'barge'];

function formatDuration(ms: number): string {
  if (ms < 0) return '00:00';
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export function SupervisorMonitor() {
  const { user, effectiveRole, isImpersonating } = useAuth();

  // Mirror the controller state into React. The snapshot object identity only
  // changes when something the UI cares about moves, so this re-renders
  // precisely on real transitions.
  const snap = useSyncExternalStore(subscribe, getSnapshot);

  const [open, setOpen] = useState(false);
  // 1s tick so call durations advance while the panel is open.
  const [, setTick] = useState(0);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Whether this user may monitor. Platform-ops always can (unless they're
  // impersonating a lower role); otherwise the effective role must be in the
  // supervisor set. Mirrors the legacy canSupervise() check.
  const canSupervise =
    !!user &&
    ((user.platformOps === true && !isImpersonating) || SUPERVISOR_ROLES.has(effectiveRole));

  // Register voice listeners once and tell the controller who we are (so it
  // can match supervisor.* WS events to this session). Safe to call for
  // non-supervisors too, but we gate to avoid needless work.
  useEffect(() => {
    if (!canSupervise) return;
    setMonitorIdentity(user?.sub ?? null);
    registerVoiceEvents();
  }, [canSupervise, user?.sub]);

  // Start/stop active-call polling with the panel.
  useEffect(() => {
    if (!open) return;
    openPanel();
    return () => closePanel();
  }, [open]);

  // Duration ticker — only while open and there are calls to show.
  useEffect(() => {
    if (!open || snap.activeCalls.length === 0) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [open, snap.activeCalls.length]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        panelRef.current &&
        !panelRef.current.contains(t) &&
        btnRef.current &&
        !btnRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const onJoin = useCallback((callId: string, mode: SupervisorMode) => {
    void joinCall(callId, mode);
  }, []);

  if (!canSupervise) return null;

  const { activeCalls, monitorSession, joining } = snap;
  const monitoring = monitorSession !== null;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className={`relative p-2 rounded-full border transition-colors cursor-pointer ${
          monitoring
            ? 'bg-primary/10 text-primary border-primary/30'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground border-transparent'
        }`}
        title="Supervisor monitor"
        aria-label="Supervisor monitor"
        aria-expanded={open}
      >
        <Ear className="w-4 h-4 stroke-[1.5]" />
        {activeCalls.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-white text-[0.5625rem] font-bold flex items-center justify-center border-2 border-card">
            {activeCalls.length > 9 ? '9+' : activeCalls.length}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute top-full right-0 mt-1.5 w-[340px] bg-card border border-border/80 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] z-50 overflow-hidden"
        >
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/40">
            <span className="text-[0.8125rem] font-semibold text-foreground">Active Calls</span>
            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-secondary text-muted-foreground text-[0.625rem] font-bold flex items-center justify-center">
              {activeCalls.length}
            </span>
          </div>

          <div className="max-h-[360px] overflow-y-auto py-1">
            {activeCalls.length === 0 ? (
              <div className="px-4 py-6 text-center text-[0.75rem] text-muted-foreground italic">
                No active calls
              </div>
            ) : (
              activeCalls.map((c) => (
                <CallCard
                  key={c.id}
                  call={c}
                  monitoredMode={monitorSession?.callId === c.id ? monitorSession.mode : null}
                  anyMonitoring={monitoring}
                  joining={joining}
                  onJoin={onJoin}
                  onChangeMode={(m) => void changeMode(m)}
                  onLeave={() => void leaveCall()}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CallCard({
  call,
  monitoredMode,
  anyMonitoring,
  joining,
  onJoin,
  onChangeMode,
  onLeave,
}: {
  call: SupervisorCallInfo;
  monitoredMode: SupervisorMode | null;
  anyMonitoring: boolean;
  joining: boolean;
  onJoin: (callId: string, mode: SupervisorMode) => void;
  onChangeMode: (mode: SupervisorMode) => void;
  onLeave: () => void;
}) {
  const duration = call.answered_at
    ? formatDuration(Date.now() - new Date(call.answered_at).getTime())
    : '--:--';
  const statusLabel = call.status === 'in-progress' ? 'Connected' : call.status;
  const isMonitored = monitoredMode !== null;
  // Join controls show only on in-progress calls when not already in a session.
  const showJoin = !anyMonitoring && call.status === 'in-progress' && !joining;

  return (
    <div
      className={`mx-2 my-1 rounded-lg border px-3 py-2 ${
        isMonitored ? 'border-primary/40 bg-primary/5' : 'border-transparent'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[0.75rem] font-medium text-foreground truncate">
            {call.from_number} → {call.to_number}
          </div>
          <div className="text-[0.625rem] text-muted-foreground">
            {!isMonitored && <span className="capitalize">{call.direction} · </span>}
            {statusLabel} · <span className="tabular-nums">{duration}</span>
          </div>
        </div>
        {isMonitored && (
          <span className="text-[0.5625rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary text-white">
            {monitoredMode}
          </span>
        )}
      </div>

      {showJoin && (
        <div className="mt-2 flex gap-1.5">
          {MODES.map((m) => (
            <button
              key={m}
              onClick={() => onJoin(call.id, m)}
              className="flex-1 h-7 rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 text-[0.6875rem] font-medium capitalize cursor-pointer"
            >
              {m}
            </button>
          ))}
        </div>
      )}

      {isMonitored && (
        <div className="mt-2 flex gap-1.5">
          {MODES.map((m) => (
            <button
              key={m}
              onClick={() => onChangeMode(m)}
              disabled={m === monitoredMode}
              className={`flex-1 h-7 rounded-md text-[0.6875rem] font-medium capitalize transition-colors ${
                m === monitoredMode
                  ? 'bg-primary text-primary-foreground cursor-default'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer'
              }`}
            >
              {m}
            </button>
          ))}
          <button
            onClick={onLeave}
            className="flex-1 h-7 rounded-md bg-destructive/10 text-destructive hover:bg-destructive/20 text-[0.6875rem] font-medium cursor-pointer"
          >
            Leave
          </button>
        </div>
      )}

      {joining && !anyMonitoring && (
        <div className="mt-2 text-[0.625rem] text-muted-foreground italic">Connecting…</div>
      )}
    </div>
  );
}
